import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  ChevronRight, ChevronsRight, Play, Pause, RotateCcw, TrendingUp, TrendingDown,
  Hand, Lock, X, Sparkles, Bot, ArrowRight, Info, Loader2,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import ChartEngine from "@/components/ChartEngine";
import Disclaimer from "@/components/Disclaimer";
import { scenarioTitle, scenarioSubtitle } from "@/lib/scenarioTitle";
import { UnlockList } from "@/components/mission/UnlockCard";
import { BossRetry, StageUnlocked } from "@/components/mission/Boss";
import { toast } from "sonner";
import { CallCardNotice } from "@/components/calls/CallCardNotice";

// Normal prices keep 2 decimals; sub-$100 assets (e.g. DOGE in a pack) keep real precision.
const px = (n) => (n >= 100 ? +n.toFixed(2) : +n.toPrecision(5));

const CONFLUENCE_FACTORS = [
  "Market Structure", "Higher Timeframe", "Support", "Resistance", "VWAP",
  "EMA", "Volume", "Momentum", "Money Flow", "RSI", "Divergence", "Volatility",
];
const CHECKLIST = [
  ["identified_structure", "I identified market structure."],
  ["checked_higher_timeframe", "I checked the higher timeframe."],
  ["identified_sr", "I identified important support / resistance."],
  ["know_invalidation", "I know where my idea becomes invalid."],
  ["stop_is_invalidation", "My stop represents that invalidation."],
  ["defined_target", "I defined my target."],
  ["calculated_risk", "I calculated my risk."],
  ["not_fomo", "I am not entering because of FOMO."],
];
const EMOTIONS = ["CALM", "FOCUSED", "CONFIDENT", "EXCITED", "NERVOUS", "FOMO", "FRUSTRATED", "IMPATIENT"];
const SPEEDS = { "1x": 1000, "2x": 500, "5x": 200 };

const DEFAULT_TOGGLES = { volume: true, vwap: true, ema9: false, ema20: true, ema50: false, ema200: false, rsi: true };

function calcRisk(direction, entry, stop, target, balance, riskPct) {
  const maxRisk = (balance * riskPct) / 100;
  let rpu, rwu;
  if (direction === "LONG") { rpu = entry - stop; rwu = target - entry; }
  else { rpu = stop - entry; rwu = entry - target; }
  const valid = rpu > 0 && rwu > 0 && entry > 0;
  const qty = valid ? maxRisk / rpu : 0;
  return { valid, maxRisk, rpu, rwu, qty, rr: valid ? rwu / rpu : 0, loss: valid ? rpu * qty : 0, profit: valid ? rwu * qty : 0 };
}

export default function Replay() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const missionId = params.get("mission");
  const scenarioParam = params.get("scenario");
  const [sessions, setSessions] = useState([]);
  const [missionResult, setMissionResult] = useState(null);
  const [state, setState] = useState(null); // replay state from server
  const [toggles, setToggles] = useState(DEFAULT_TOGGLES);
  const [balance, setBalance] = useState(10000);
  const [riskPct, setRiskPct] = useState(1);
  const [decision, setDecision] = useState(null);
  const [plan, setPlan] = useState({ entry: "", stop: "", target: "", thesis: "", setup: "", confidence: 3, emotional_state: "CALM" });
  const [confluence, setConfluence] = useState({});
  const [checklist, setChecklist] = useState({});
  const [autoplay, setAutoplay] = useState(false);
  const [speed, setSpeed] = useState("2x");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // score/journal result
  const [journal, setJournal] = useState({});
  const [showJournal, setShowJournal] = useState(false);
  const timerRef = useRef(null);

  const trade = state?.trade;
  const status = state?.status;
  const atEnd = state?.at_end;
  const lastClose = state?.candles?.length ? state.candles[state.candles.length - 1].close : 0;

  useEffect(() => {
    api.get("/market/sessions").then((r) => setSessions(r.data.sessions)).catch(() => {});
    api.get("/settings").then((r) => { setBalance(r.data.balance); setRiskPct(r.data.risk_percent); }).catch(() => {});
  }, []);

  // Launch a specific scenario when arriving from a Trader Path mission
  useEffect(() => {
    if (scenarioParam) startSession(scenarioParam);
    // eslint-disable-next-line
  }, [scenarioParam]);

  const startSession = async (session_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/replay/start", { session_id, mission_id: missionId });
      setState(data); resetTradeUi();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const resetTradeUi = () => {
    setDecision(null); setResult(null); setShowJournal(false); setJournal({});
    setConfluence({}); setChecklist({});
    setPlan({ entry: "", stop: "", target: "", thesis: "", setup: "", confidence: 3, emotional_state: "CALM" });
  };

  const advance = useCallback(async (count) => {
    if (!state) return;
    try {
      const { data } = await api.post("/replay/advance", { attempt_id: state.attempt_id, count });
      setState(data);
      if (data.trade && data.trade.status === "CLOSED" && trade?.status === "ACTIVE") {
        setAutoplay(false);
        toast.info(`Trade closed: ${data.trade.exit_reason} (${data.trade.pnl >= 0 ? "+" : ""}$${data.trade.pnl})`);
      }
    } catch (e) { toast.error(apiErr(e)); }
  }, [state, trade]);

  // autoplay
  useEffect(() => {
    if (autoplay && state && !atEnd) {
      timerRef.current = setInterval(() => advance(1), SPEEDS[speed]);
      return () => clearInterval(timerRef.current);
    }
    clearInterval(timerRef.current);
    if (atEnd) setAutoplay(false);
  }, [autoplay, speed, advance, state, atEnd]);

  // keyboard shortcuts
  useEffect(() => {
    const h = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (!state) return;
      if (e.code === "Space") { e.preventDefault(); advance(1); }
      if (e.key === "l" || e.key === "L") chooseDecision("LONG");
      if (e.key === "s" || e.key === "S") chooseDecision("SHORT");
      if (e.key === "p" || e.key === "P") chooseDecision("PASS");
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [state, advance]);

  const chooseDecision = (d) => {
    if (trade) return;
    setDecision(d);
    if ((d === "LONG" || d === "SHORT") && lastClose) {
      setPlan((p) => ({
        ...p, entry: lastClose,
        stop: d === "LONG" ? px(lastClose * 0.985) : px(lastClose * 1.015),
        target: d === "LONG" ? px(lastClose * 1.03) : px(lastClose * 0.97),
      }));
    }
  };

  const risk = decision && decision !== "PASS"
    ? calcRisk(decision, +plan.entry, +plan.stop, +plan.target, balance, riskPct)
    : null;

  const checklistDone = CHECKLIST.every(([k]) => checklist[k]);

  const lockPlan = async () => {
    if (!risk?.valid) { toast.error("Fix your entry / stop / target first."); return; }
    setBusy(true);
    try {
      const confluenceArr = Object.entries(confluence).map(([factor, s]) => ({ factor, state: s }));
      const { data } = await api.post("/trade/lock", {
        attempt_id: state.attempt_id, direction: decision,
        entry: +plan.entry, stop: +plan.stop, target: +plan.target,
        setup: plan.setup, thesis: plan.thesis, confidence: plan.confidence,
        confluence: confluenceArr, checklist, emotional_state: plan.emotional_state,
      });
      toast.success("Plan locked. Advance candles to see it play out.");
      const st = await api.get(`/replay/${state.attempt_id}`);
      setState(st.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submitPass = async () => {
    setBusy(true);
    try {
      const confluenceArr = Object.entries(confluence).map(([factor, s]) => ({ factor, state: s }));
      const { data } = await api.post("/trade/pass", { attempt_id: state.attempt_id, thesis: plan.thesis, confluence: confluenceArr });
      setResult({ pass: true, ...data });
      if (data.result.correct) toast.success(data.result.headline);
      else toast.info(data.result.headline);
      if (missionId) {
        const mc = await api.post("/training/market-complete", { mission_id: missionId, attempt_id: state.attempt_id });
        setMissionResult(mc.data);
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const closeTrade = async () => {
    setBusy(true);
    try {
      await api.post("/trade/close", { attempt_id: state.attempt_id });
      const st = await api.get(`/replay/${state.attempt_id}`);
      setState(st.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submitJournal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/journal", {
        trade_id: trade.id, emotional_state: plan.emotional_state, answers: journal,
      });
      setResult({ ...data });
      setShowJournal(false);
      toast.success(`Process score: ${data.score.total}/100`);
      if (missionId) {
        const mc = await api.post("/training/market-complete", { mission_id: missionId, attempt_id: trade.attempt_id || state.attempt_id });
        setMissionResult(mc.data);
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const nextScenario = () => { setState(null); resetTradeUi(); setAutoplay(false); };

  // ---------------- session picker ----------------
  if (!state) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
        <Header title="Market Replay" sub="Open a historical BTC/ETH chart and trade it candle by candle. The future is hidden — no look-ahead." />
        <ScenarioGrid sessions={sessions} busy={busy} onStart={startSession} navigate={navigate} />
        <Disclaimer />
      </div>
    );
  }

  // ---------------- replay workspace ----------------
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="back-to-sessions" onClick={nextScenario} className="text-sm text-tc-muted hover:text-tc-txt flex items-center gap-1.5">
          <X className="w-4 h-4" /> Exit scenario
        </button>
        <StateBadge status={status} />
      </div>
      {state.session?.pack_name && (
        <div data-testid="pack-banner" className="tc-card p-4 mb-4 border-tc-xp/30 bg-tc-xp/5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-tc-xp">{state.session.pack_name} · {state.session.date_window}</div>
          <div className="text-sm font-semibold mt-1">{state.session.pack_lesson}</div>
          {state.session.pack_header && <div data-testid="pack-scenario-header" className="text-xs text-tc-sub mt-1">{state.session.pack_header}</div>}
          <div data-testid="pack-provenance" className="text-[11px] font-mono text-tc-muted mt-1">{state.session.symbol} · {state.session.interval} · {state.session.source}{state.session.source_symbol ? ` (${state.session.source_symbol})` : ""} · {state.session.date_window}</div>
          <div className="text-[11px] text-tc-muted mt-1">You are replaying real {state.session.symbol} crypto data from this window. It is not a stock chart. Candles are revealed one at a time, so nothing after the decision point is shown.</div>
        </div>
      )}

      {state.is_real && (
        <div data-testid="classified-banner" className="tc-card p-4 mb-4 border-tc-coach/30 bg-gradient-to-r from-tc-coach/10 to-transparent">
          <div className="flex items-center gap-2 text-tc-coach text-xs font-mono uppercase tracking-widest">
            <Info className="w-3.5 h-3.5" /> {state.reveal ? "MARKET REVEALED" : "REAL HISTORICAL MARKET — DETAILS HIDDEN"}
          </div>
          {!state.reveal ? (
            <p className="text-sm text-tc-sub mt-1">This market actually happened. Everything after the current candle is hidden. History already knows what happened next — you don't. Analyze what you can see.</p>
          ) : (
            <div className="mt-1 text-sm grid sm:grid-cols-2 gap-x-6 gap-y-0.5 font-mono">
              <KV2 k="Asset" v={state.reveal.symbol} /><KV2 k="Source" v={state.reveal.source} />
              <KV2 k="Interval" v={state.reveal.interval} /><KV2 k="Category" v={state.reveal.category} />
              <KV2 k="Start (UTC)" v={state.reveal.start_utc} /><KV2 k="End (UTC)" v={state.reveal.end_utc} />
              <div className="sm:col-span-2 text-[11px] text-tc-muted mt-1">This is REAL historical market data. Knowing how a market behaved in the past does not predict future performance.</div>
            </div>
          )}
        </div>
      )}

      <div className="grid xl:grid-cols-[1fr_400px] gap-6">
        {/* Chart + controls */}
        <div className="space-y-4">
          <div className="tc-card p-4">
            <ChartEngine candles={state.candles} toggles={toggles} symbol={state.session.symbol}
              interval={state.session.interval} badge={state.is_real ? "REAL HISTORY" : "SIMULATED"}
              overlays={trade ? { entry: trade.entry, stop: trade.current_stop, target: trade.current_target } : null} />
            {/* indicator toggles */}
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.keys(DEFAULT_TOGGLES).map((k) => (
                <button key={k} data-testid={`toggle-${k}`} onClick={() => setToggles((t) => ({ ...t, [k]: !t[k] }))}
                  className={`text-[11px] font-mono px-2.5 py-1 rounded-md border transition-colors ${
                    toggles[k] ? "bg-tc-coach/15 border-tc-coach/40 text-tc-coach" : "border-white/10 text-tc-muted hover:text-tc-sub"}`}>
                  {k.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* replay controls */}
          <div className="tc-card p-4 flex flex-wrap items-center gap-2">
            <button data-testid="replay-next-candle-btn" disabled={atEnd} onClick={() => advance(1)}
              className="flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-40">
              <ChevronRight className="w-4 h-4" /> Next
            </button>
            <button data-testid="replay-plus5-btn" disabled={atEnd} onClick={() => advance(5)}
              className="flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-40">
              <ChevronsRight className="w-4 h-4" /> +5
            </button>
            <button data-testid="replay-autoplay-btn" disabled={atEnd} onClick={() => setAutoplay((a) => !a)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm border disabled:opacity-40 ${
                autoplay ? "bg-tc-bear/15 border-tc-bear/40 text-tc-bear" : "bg-tc-bull/15 border-tc-bull/40 text-tc-bull"}`}>
              {autoplay ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Autoplay</>}
            </button>
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              {Object.keys(SPEEDS).map((sp) => (
                <button key={sp} data-testid={`speed-${sp}`} onClick={() => setSpeed(sp)}
                  className={`px-2.5 py-2 text-xs font-mono ${speed === sp ? "bg-tc-coach text-tc-bg" : "text-tc-muted hover:text-tc-sub"}`}>{sp}</button>
              ))}
            </div>
            <button data-testid="replay-reset-btn" onClick={async () => {
              const { data } = await api.post("/replay/reset", { session_id: state.attempt_id });
              setState(data); resetTradeUi();
            }} className="flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm hover:bg-white/10 ml-auto">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
            <span className="text-[11px] font-mono text-tc-muted w-full sm:w-auto">
              Candle {state.reveal_index + 1} / {state.total_candles} · Space = next
            </span>
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          {result ? (
            <ScoreCard result={result} trade={trade} onNext={nextScenario} onCoach={() => navigate("/coach")}
              missionResult={missionResult} navigate={navigate} />
          ) : trade && trade.status === "ACTIVE" ? (
            <ActiveTrade trade={trade} onClose={closeTrade} busy={busy} />
          ) : trade && trade.status === "CLOSED" ? (
            <ClosedTrade trade={trade} onJournal={() => setShowJournal(true)} />
          ) : (
            <DecisionPanel {...{ decision, chooseDecision, plan, setPlan, risk, confluence, setConfluence,
              checklist, setChecklist, checklistDone, lockPlan, submitPass, busy, session: state.session }} />
          )}
        </div>
      </div>

      {showJournal && (
        <JournalModal journal={journal} setJournal={setJournal} plan={plan} setPlan={setPlan}
          onSubmit={submitJournal} onClose={() => setShowJournal(false)} busy={busy} trade={trade} />
      )}
    </div>
  );
}

/* ---------------- sub components ---------------- */
function Header({ title, sub }) {
  return (
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
      <p className="text-tc-sub mt-1 max-w-2xl">{sub}</p>
    </div>
  );
}

function StateBadge({ status }) {
  const map = {
    READY: "text-tc-coach bg-tc-coach/10", TRADE_ACTIVE: "text-tc-xp bg-tc-xp/10",
    REVIEW: "text-tc-mastery bg-tc-mastery/10", COMPLETED: "text-tc-bull bg-tc-bull/10",
  };
  return <span data-testid="replay-state" className={`text-[11px] font-mono px-2.5 py-1 rounded-md tracking-widest ${map[status] || "text-tc-sub bg-white/5"}`}>{status}</span>;
}

function DecisionPanel({ decision, chooseDecision, plan, setPlan, risk, confluence, setConfluence, checklist, setChecklist, checklistDone, lockPlan, submitPass, busy, session }) {
  const cycle = (f) => setConfluence((c) => {
    const order = { undefined: "support", support: "caution", caution: "neutral", neutral: undefined };
    const next = order[c[f]];
    const n = { ...c }; if (next) n[f] = next; else delete n[f]; return n;
  });
  const supporting = Object.values(confluence).filter((s) => s === "support").length;
  const cautions = Object.values(confluence).filter((s) => s === "caution").length;

  return (
    <>
      {/* Confluence Builder */}
      <div className="tc-card p-5">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-tc-coach">
          <Sparkles className="w-3.5 h-3.5" /> Confluence Builder
        </div>
        <p className="text-[11px] text-tc-muted mt-1">Click to cycle: ✓ support · ⚠ caution · ○ neutral</p>
        <div className="grid grid-cols-2 gap-1.5 mt-3">
          {CONFLUENCE_FACTORS.map((f) => {
            const s = confluence[f];
            const style = s === "support" ? "text-tc-bull border-tc-bull/40 bg-tc-bull/10"
              : s === "caution" ? "text-tc-pass border-tc-pass/40 bg-tc-pass/10"
              : s === "neutral" ? "text-tc-sub border-white/20 bg-white/5" : "text-tc-muted border-white/10";
            const icon = s === "support" ? "✓" : s === "caution" ? "⚠" : s === "neutral" ? "○" : "+";
            return (
              <button key={f} data-testid={`confluence-${f.replace(/\s/g, "-").toLowerCase()}`} onClick={() => cycle(f)}
                className={`text-[11px] px-2 py-1.5 rounded-md border text-left flex items-center gap-1.5 transition-colors ${style}`}>
                <span className="font-mono w-3">{icon}</span>{f}
              </button>
            );
          })}
        </div>
        <div className="mt-3 text-sm">
          <span className="text-tc-bull font-mono">{supporting} supporting</span>
          {cautions > 0 && <span className="text-tc-pass font-mono"> · {cautions} caution</span>}
        </div>
        <div className="mt-2 text-center font-heading font-bold text-tc-txt text-sm py-2 rounded-lg bg-white/5">
          DOES THE EVIDENCE JUSTIFY THE RISK?
        </div>
      </div>

      {/* Decision */}
      <div className="tc-card p-5">
        <div className="text-xs font-mono uppercase tracking-widest text-tc-sub mb-3">What would you do?</div>
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="decision-long-btn" onClick={() => chooseDecision("LONG")}
            className={`flex flex-col items-center gap-1 py-3 rounded-lg border transition-all ${decision === "LONG" ? "bg-tc-bull/20 border-tc-bull text-tc-bull" : "border-white/10 text-tc-sub hover:border-tc-bull/40"}`}>
            <TrendingUp className="w-5 h-5" /><span className="text-sm font-semibold">LONG</span>
          </button>
          <button data-testid="decision-short-btn" onClick={() => chooseDecision("SHORT")}
            className={`flex flex-col items-center gap-1 py-3 rounded-lg border transition-all ${decision === "SHORT" ? "bg-tc-bear/20 border-tc-bear text-tc-bear" : "border-white/10 text-tc-sub hover:border-tc-bear/40"}`}>
            <TrendingDown className="w-5 h-5" /><span className="text-sm font-semibold">SHORT</span>
          </button>
          <button data-testid="decision-pass-btn" onClick={() => chooseDecision("PASS")}
            className={`flex flex-col items-center gap-1 py-3 rounded-lg border transition-all ${decision === "PASS" ? "bg-tc-pass/20 border-tc-pass text-tc-pass" : "border-tc-pass/25 text-tc-pass/80 hover:border-tc-pass/50"}`}>
            <Hand className="w-5 h-5" /><span className="text-sm font-semibold">PASS</span>
          </button>
        </div>
        <p className="text-[11px] text-tc-muted mt-2 text-center">PASS is a first-class decision. Sometimes the best trade is no trade.</p>
      </div>

      {decision === "PASS" && (
        <div className="tc-card p-5 space-y-3">
          <div className="text-sm text-tc-sub">Why are you passing on this setup? In your own words. This is saved with your call; a Call Card needs at least 10 characters.</div>
          <textarea data-testid="pass-thesis" value={plan.thesis} onChange={(e) => setPlan({ ...plan, thesis: e.target.value })}
            placeholder="e.g. Choppy range, no clear structure, evidence does not justify the risk."
            className="w-full h-20 rounded-lg bg-tc-bg border border-white/10 px-3 py-2 text-sm outline-none focus:border-tc-pass" />
          <button data-testid="submit-pass-btn" disabled={busy} onClick={submitPass}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-tc-pass text-white font-semibold py-2.5 hover:bg-tc-pass/90 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} <Hand className="w-4 h-4" /> Confirm PASS
          </button>
        </div>
      )}

      {(decision === "LONG" || decision === "SHORT") && (
        <div className="tc-card p-5 space-y-4">
          <div className="text-xs font-mono uppercase tracking-widest text-tc-sub">Trade Plan · {decision}</div>
          <div className="grid grid-cols-3 gap-2">
            {["entry", "stop", "target"].map((f) => (
              <div key={f}>
                <label className="text-[10px] font-mono uppercase text-tc-muted">{f}</label>
                <input data-testid={`plan-${f}`} type="number" value={plan[f]} onChange={(e) => setPlan({ ...plan, [f]: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-tc-bg border border-white/10 px-2 py-2 text-sm font-mono outline-none focus:border-tc-coach" />
              </div>
            ))}
          </div>

          {/* live risk */}
          <div className={`rounded-lg p-3 text-sm ${risk?.valid ? "bg-white/5" : "bg-tc-bear/10 border border-tc-bear/30"}`} data-testid="risk-panel">
            {risk?.valid ? (
              <div className="grid grid-cols-2 gap-y-1.5 font-mono text-[13px]">
                <Row l="Max risk" v={`$${risk.maxRisk.toFixed(2)}`} />
                <Row l="Position" v={`${risk.qty.toFixed(6)}`} />
                <Row l="Risk / unit" v={`$${risk.rpu.toFixed(2)}`} />
                <Row l="R:R" v={`${risk.rr.toFixed(2)}`} accent={risk.rr >= 2 ? "text-tc-bull" : risk.rr >= 1.5 ? "text-tc-xp" : "text-tc-bear"} />
                <Row l="Potential loss" v={`-$${risk.loss.toFixed(2)}`} accent="text-tc-bear" />
                <Row l="Potential profit" v={`+$${risk.profit.toFixed(2)}`} accent="text-tc-bull" />
              </div>
            ) : (
              <div className="flex items-start gap-2 text-tc-bearlight text-xs"><Info className="w-4 h-4 shrink-0 mt-0.5" />
                {decision === "LONG" ? "For a LONG: stop below entry, target above entry." : "For a SHORT: stop above entry, target below entry."}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <input data-testid="plan-setup" value={plan.setup} onChange={(e) => setPlan({ ...plan, setup: e.target.value })}
              placeholder="Setup (e.g. pullback)" className="rounded-lg bg-tc-bg border border-white/10 px-3 py-2 text-sm outline-none focus:border-tc-coach" />
            <select data-testid="plan-emotion" value={plan.emotional_state} onChange={(e) => setPlan({ ...plan, emotional_state: e.target.value })}
              className="rounded-lg bg-tc-bg border border-white/10 px-3 py-2 text-sm outline-none focus:border-tc-coach">
              {EMOTIONS.map((em) => <option key={em} value={em}>{em}</option>)}
            </select>
          </div>
          <textarea data-testid="plan-thesis" value={plan.thesis} onChange={(e) => setPlan({ ...plan, thesis: e.target.value })}
            placeholder="Why this trade, in your own words, and where it becomes invalid. Saved with your call; a Call Card needs at least 10 characters."
            className="w-full h-16 rounded-lg bg-tc-bg border border-white/10 px-3 py-2 text-sm outline-none focus:border-tc-coach" />

          {/* checklist */}
          <div className="space-y-1.5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted">Pre-Trade Checklist</div>
            {CHECKLIST.map(([k, label]) => (
              <label key={k} data-testid={`checklist-${k}`} className="flex items-start gap-2 text-[13px] text-tc-sub cursor-pointer">
                <input type="checkbox" checked={!!checklist[k]} onChange={(e) => setChecklist({ ...checklist, [k]: e.target.checked })}
                  className="mt-0.5 accent-tc-coach" />
                {label}
              </label>
            ))}
          </div>

          <button data-testid="lock-plan-btn" disabled={busy || !risk?.valid || !checklistDone} onClick={lockPlan}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 hover:bg-tc-coach/90 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Lock Trade Plan
          </button>
          {!checklistDone && <p className="text-[11px] text-tc-muted text-center">Complete the checklist to lock your plan.</p>}
        </div>
      )}
    </>
  );
}

function Row({ l, v, accent = "text-tc-txt" }) {
  return (<><span className="text-tc-muted">{l}</span><span className={`text-right ${accent}`}>{v}</span></>);
}

function KV2({ k, v }) {
  return (<><span className="text-tc-muted">{k}: <span className="text-tc-txt">{v || "—"}</span></span></>);
}

const DIFF_COLOR = { BEGINNER: "text-tc-bull bg-tc-bull/10", INTERMEDIATE: "text-tc-xp bg-tc-xp/10", ADVANCED: "text-tc-bear bg-tc-bear/10" };

function ScenarioGrid({ sessions, busy, onStart }) {
  const real = sessions.filter((s) => !s.is_synthetic);
  const demo = sessions.filter((s) => s.is_synthetic);
  const Card = ({ s }) => (
    <button data-testid={`session-${s.id}`} disabled={busy} onClick={() => onStart(s.id)}
      className="tc-card p-5 text-left hover:border-tc-coach/40 hover:-translate-y-0.5 transition-all group">
      <div className="flex items-center justify-between">
        <span className="font-heading font-bold text-lg">{s.symbol}</span>
        {s.is_synthetic ? (
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-tc-pass/15 text-tc-pass">{s.interval} · SIMULATED</span>
        ) : (
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-tc-coach/15 text-tc-coach">{s.interval} · REAL HISTORY</span>
        )}
      </div>
      <div className="font-semibold mt-2" data-testid={`session-title-${s.id}`}>{scenarioTitle(s)}</div>
      {!s.is_synthetic && <div className="text-[11px] font-mono text-tc-muted mt-0.5">{scenarioSubtitle(s)}</div>}
      <div className="text-sm text-tc-sub mt-1 leading-relaxed">{s.is_synthetic ? s.description : s.teaching_objective}</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!s.is_synthetic && s.category && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-tc-sub">{s.category}</span>}
        {!s.is_synthetic && s.difficulty && <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${DIFF_COLOR[s.difficulty] || "text-tc-sub bg-white/5"}`}>{s.difficulty}</span>}
        {s.completed && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-tc-bull/15 text-tc-bull">COMPLETED</span>}
      </div>
      <div className="mt-3 flex items-center gap-2 text-tc-coach text-sm font-medium">
        {s.is_synthetic ? "Start Replay" : "Begin Analysis"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </div>
    </button>
  );
  return (
    <div className="space-y-5">
      {real.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-mono uppercase tracking-widest text-tc-coach">Real Historical Markets</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-tc-coach/15 text-tc-coach">{real.length}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{real.map((s) => <Card key={s.id} s={s} />)}</div>
        </div>
      )}
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-tc-pass mb-3">Demo / Simulated Markets</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{demo.map((s) => <Card key={s.id} s={s} />)}</div>
      </div>
    </div>
  );
}

function ActiveTrade({ trade, onClose, busy }) {
  const up = (trade.unrealized_pnl ?? 0) >= 0;
  return (
    <div className="tc-card p-5 space-y-3" data-testid="active-trade">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-widest text-tc-xp">Trade Active · {trade.direction}</span>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-tc-xp/15 text-tc-xp">LOCKED</span>
      </div>
      <div className="grid grid-cols-3 gap-2 font-mono text-sm">
        <Mini l="Entry" v={trade.entry} /><Mini l="Stop" v={trade.current_stop} accent="text-tc-bear" /><Mini l="Target" v={trade.current_target} accent="text-tc-bull" />
      </div>
      <div className="rounded-lg bg-white/5 p-3 text-center">
        <div className="text-[10px] font-mono uppercase text-tc-muted">Unrealized P&L</div>
        <div className={`font-mono text-2xl font-semibold ${up ? "text-tc-bull" : "text-tc-bear"}`} data-testid="unrealized-pnl">
          {up ? "+" : ""}${(trade.unrealized_pnl ?? 0).toFixed(2)}
        </div>
        <div className={`text-xs font-mono ${up ? "text-tc-bull" : "text-tc-bear"}`}>{(trade.unrealized_r ?? 0).toFixed(2)}R</div>
      </div>
      <p className="text-[11px] text-tc-muted">Advance candles to let stop or target trigger — or exit manually (early exits affect discipline).</p>
      <button data-testid="close-trade-btn" disabled={busy} onClick={onClose}
        className="w-full rounded-lg bg-white/5 border border-white/10 py-2.5 text-sm hover:bg-white/10 disabled:opacity-50">
        Exit Trade Manually
      </button>
    </div>
  );
}

function Mini({ l, v, accent = "text-tc-txt" }) {
  return (<div className="rounded-lg bg-white/5 p-2 text-center"><div className="text-[9px] font-mono uppercase text-tc-muted">{l}</div><div className={`text-sm ${accent}`}>{v}</div></div>);
}

function ClosedTrade({ trade, onJournal }) {
  const up = (trade.pnl ?? 0) >= 0;
  return (
    <div className="tc-card p-5 space-y-3" data-testid="closed-trade">
      <span className="text-xs font-mono uppercase tracking-widest text-tc-mastery">Trade Closed · {trade.exit_reason}</span>
      <div className="rounded-lg bg-white/5 p-4 text-center">
        <div className={`font-mono text-3xl font-bold ${up ? "text-tc-bull" : "text-tc-bear"}`}>{up ? "+" : ""}${(trade.pnl ?? 0).toFixed(2)}</div>
        <div className={`text-sm font-mono ${up ? "text-tc-bull" : "text-tc-bear"}`}>{(trade.r_multiple ?? 0).toFixed(2)}R</div>
      </div>
      {trade.raw_exit_reason === "AMBIGUOUS_INTRABAR" && (
        <div className="text-[11px] text-tc-pass bg-tc-pass/10 border border-tc-pass/30 rounded-lg p-2.5">
          This candle reached both your stop and target. OHLC data cannot tell which happened first, so TRADECRAFT used its conservative simulation rule.
        </div>
      )}
      <p className="text-sm text-tc-sub">Outcome is only part of the story. Complete your journal to unlock your <span className="text-tc-mastery">process score</span>.</p>
      <button data-testid="open-journal-btn" onClick={onJournal}
        className="w-full flex items-center justify-center gap-2 rounded-lg bg-tc-mastery text-white font-semibold py-2.5 hover:bg-tc-mastery/90">
        Complete Journal & Score <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

const JQ = [
  ["what_you_saw", "What did you see?"],
  ["why_enter", "Why did you enter?"],
  ["invalidation", "Where was invalidation?"],
  ["followed_plan", "Did you follow your plan?"],
  ["what_learned", "What did you learn?"],
  ["take_again", "Would you take this setup again?"],
  ["good_decision", "Was this a good decision regardless of outcome?"],
];

function JournalModal({ journal, setJournal, plan, setPlan, onSubmit, onClose, busy }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative tc-card p-6 w-full max-w-lg max-h-[88vh] overflow-y-auto" data-testid="journal-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-bold flex items-center gap-2"><NotebookIcon /> Trade Journal</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-tc-muted" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-mono uppercase text-tc-muted">Emotional state during trade</label>
            <select data-testid="journal-emotion" value={plan.emotional_state} onChange={(e) => setPlan({ ...plan, emotional_state: e.target.value })}
              className="mt-1 w-full rounded-lg bg-tc-bg border border-white/10 px-3 py-2 text-sm outline-none focus:border-tc-mastery">
              {EMOTIONS.map((em) => <option key={em} value={em}>{em}</option>)}
            </select>
          </div>
          {JQ.map(([k, q]) => (
            <div key={k}>
              <label className="text-[13px] text-tc-sub">{q}</label>
              <textarea data-testid={`journal-${k}`} value={journal[k] || ""} onChange={(e) => setJournal({ ...journal, [k]: e.target.value })}
                className="mt-1 w-full h-14 rounded-lg bg-tc-bg border border-white/10 px-3 py-2 text-sm outline-none focus:border-tc-mastery" />
            </div>
          ))}
        </div>
        <button data-testid="submit-journal-btn" disabled={busy} onClick={onSubmit}
          className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-tc-mastery text-white font-semibold py-2.5 hover:bg-tc-mastery/90 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit & Reveal Score
        </button>
      </div>
    </div>
  );
}
function NotebookIcon() { return <span className="text-tc-mastery">✎</span>; }

function ScoreCard({ result, trade, onNext, onCoach, missionResult, navigate }) {
  const MissionBanner = () => missionResult ? (
    <div className="space-y-3">
    {missionResult.stage_complete && <StageUnlocked copy={missionResult.stage_unlocked_copy} />}
    <BossRetry practice={missionResult.practice} />
    <UnlockList unlocks={(missionResult.new_unlocks || []).filter((u) => u.type !== "rank")} />
    <div className="tc-card p-4 border-tc-mastery/30 bg-gradient-to-r from-tc-mastery/10 to-transparent" data-testid="mission-complete-banner">
      <div className="text-[10px] font-mono uppercase tracking-widest text-tc-mastery">{missionResult.rank_unlocked ? "STAGE COMPLETE" : "MISSION COMPLETE"}</div>
      <div className="text-sm text-tc-sub mt-1">+{missionResult.xp_awarded} XP · {missionResult.stars}★</div>
      {missionResult.call_card && <div className="mt-3"><CallCardNotice result={{ call_card: missionResult.call_card }} /></div>}
      <button data-testid="mission-next-btn" onClick={() => navigate(missionResult.next_mission ? `/training/${missionResult.next_mission.id}` : "/training")}
        className="mt-3 w-full rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 text-sm hover:bg-tc-coach/90 flex items-center justify-center gap-1.5">
        {missionResult.next_mission ? `Next: ${missionResult.next_mission.name}` : "Back to Trader Path"} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
    </div>
  ) : null;

  if (result.pass) {
    const r = result.result;
    return (
      <div className="space-y-4">
      <div className="tc-card p-6 text-center space-y-3" data-testid="pass-result">
        <div className="w-14 h-14 rounded-2xl bg-tc-pass/15 border border-tc-pass/30 flex items-center justify-center mx-auto text-tc-pass">
          <Hand className="w-7 h-7" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-tc-pass">{r.headline}</h3>
        <p className="text-tc-sub text-sm">{r.message}</p>
        <div className="font-mono text-tc-xp">+{result.xp_awarded} XP</div>
        {!missionResult && <CallCardNotice result={result} />}
        {!missionResult && <button data-testid="next-scenario-btn" onClick={onNext} className="w-full rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 hover:bg-tc-coach/90">Next Scenario</button>}
      </div>
      <MissionBanner />
      </div>
    );
  }
  const b = result.score.breakdown;
  const rows = [
    ["Setup Quality", b.setup_quality], ["Risk Management", b.risk_management],
    ["Plan Quality", b.plan_quality], ["Execution Discipline", b.execution_discipline],
    ["Reflection", b.reflection], ["Outcome Analysis", b.outcome_analysis],
  ];
  return (
    <div className="tc-card p-6 space-y-4" data-testid="score-card">
      <div className="text-center">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-mastery">Process Score</div>
        <div className="font-mono text-5xl font-bold mt-1" data-testid="process-score-badge">{result.score.total}<span className="text-lg text-tc-muted">/100</span></div>
        <h3 className="font-heading text-lg font-bold mt-2">{result.headline}</h3>
        <p className="text-sm text-tc-sub mt-1">{result.sub}</p>
        <div className="text-xs font-mono text-tc-muted mt-2">
          Outcome: <span className={result.pnl >= 0 ? "text-tc-bull" : "text-tc-bear"}>{result.pnl >= 0 ? "+" : ""}${result.pnl} ({result.r_multiple}R)</span> · +{result.xp_awarded} XP
        </div>
        {!missionResult && <div className="mt-3 space-y-3"><CallCardNotice result={result} /></div>}
      </div>
      <div className="space-y-2" data-testid="score-breakdown">
        {rows.map(([label, c]) => (
          <div key={label}>
            <div className="flex justify-between text-[12px]"><span className="text-tc-sub">{label}</span><span className="font-mono">{c.score}/{c.max}</span></div>
            <div className="h-1.5 rounded-full bg-white/10 mt-1 overflow-hidden">
              <div className="h-full bg-tc-mastery rounded-full" style={{ width: `${(c.score / c.max) * 100}%` }} />
            </div>
          </div>
        ))}
        <div className="flex justify-between text-[11px] pt-1 border-t border-white/10 mt-1">
          <span className="text-tc-muted">Profit points</span><span className="font-mono text-tc-muted">0 — profit never scores</span>
        </div>
      </div>
      {result.score.violations?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted">Coaching Notes</div>
          {result.score.violations.map((v) => (
            <div key={v.code} className="text-[12px] text-tc-pass bg-tc-pass/10 rounded-md px-2.5 py-1.5">{v.label}</div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="ask-coach-btn" onClick={onCoach} className="flex items-center justify-center gap-1.5 rounded-lg bg-tc-coach/15 border border-tc-coach/30 text-tc-coach py-2.5 text-sm font-medium hover:bg-tc-coach/25">
          <Bot className="w-4 h-4" /> Ask Coach
        </button>
        <button data-testid="next-scenario-btn" onClick={onNext} className="rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 text-sm hover:bg-tc-coach/90">Next Scenario</button>
      </div>
      <MissionBanner />
    </div>
  );
}
